/**
 * Individual search result card - Apple HIG inspired design
 */
import { memo } from 'react'
import { Group, Box, Checkbox, Text, Badge } from 'components'
import { useTranslation } from 'react-i18next'
import { ILvl4Item } from 'pages/NewProject/components/ProjectSkillsStandards/interfaces'
import { SkillStandardBreadcrumb } from '../SkillStandardBreadcrumb'

interface SearchResultCardProps {
  item: ILvl4Item
  isSelected: boolean
  isAlreadyAdded: boolean
  onToggle: (item: ILvl4Item) => void
}

export const SearchResultCard = memo(({
  item, isSelected, isAlreadyAdded, onToggle
}: SearchResultCardProps) => {
  const { t } = useTranslation()

  const handleClick = () => {
    if (!isAlreadyAdded) onToggle(item)
  }

  return (
    <Box
      onClick={handleClick}
      sx={(theme) => ({
        padding: '16px 20px',
        borderRadius: theme.radius.md,
        border: `1px solid ${
          isSelected
            ? theme.colors.teal[5]
            : theme.colorScheme === 'dark'
              ? theme.colors.dark[4]
              : theme.colors.gray[3]
        }`,
        backgroundColor: isSelected
          ? theme.colorScheme === 'dark'
            ? theme.fn.rgba(theme.colors.teal[9], 0.15)
            : theme.colors.teal[0]
          : isAlreadyAdded
            ? theme.colorScheme === 'dark'
              ? theme.colors.dark[6]
              : theme.colors.gray[0]
            : theme.colorScheme === 'dark'
              ? theme.colors.dark[6]
              : theme.white,
        boxShadow: theme.shadows.sm,
        cursor: isAlreadyAdded ? 'default' : 'pointer',
        opacity: isAlreadyAdded ? 0.6 : 1,
        transition: 'all 0.2s ease',
        '&:hover': !isAlreadyAdded ? {
          boxShadow: theme.shadows.md,
          borderColor: isSelected
            ? theme.colors.teal[5]
            : theme.colors.gray[4],
          backgroundColor: isSelected
            ? undefined
            : theme.colorScheme === 'dark'
              ? theme.colors.dark[5]
              : theme.colors.gray[0],
        } : undefined,
      })}
    >
      <Group align="flex-start" noWrap spacing="md">
        <Checkbox
          checked={isSelected || isAlreadyAdded}
          disabled={isAlreadyAdded}
          onChange={() => !isAlreadyAdded && onToggle(item)}
          onClick={(e) => e.stopPropagation()}
          size="md"
          radius="sm"
          color="teal"
          styles={(theme) => ({
            input: {
              cursor: isAlreadyAdded ? 'default' : 'pointer',
              borderWidth: 2,
            }
          })}
        />
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          <SkillStandardBreadcrumb
            item={item}
            showCyclesBadges={false}
            fullBreadCrumb={true}
          />
          {isAlreadyAdded && (
            <Badge
              size="xs"
              color="gray"
              variant="light"
              mt={8}
              radius="sm"
            >
              {t('arc_already_added')}
            </Badge>
          )}
        </Box>
      </Group>
    </Box>
  )
})

SearchResultCard.displayName = 'SearchResultCard'
